import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faUnlink, faLink } from '@fortawesome/pro-solid-svg-icons';
import { faFolder } from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  GetIntSettingsResponse,
  IntFolder,
  TIntegrationType,
} from '@gen2/api/integrations/api';
import {
  IntegrationKeys,
  useStorageFolderMutation,
  useStorageSettings,
} from '@gen2/api/integrations/hooks';
import { ActionModal } from '@gen2/app/components/action-modal/action-modal';
import { useActionModalStore } from '@gen2/app/components/action-modal/store';
import { queryClient } from '@gen2/config';
import { PermissionsKeys } from '@gen2/types/permissions';
import { usePermissions } from '@gen2/utils/permissions/permissions';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Button } from '@mui/material';
import { ModalCloseButton } from '@nx-fe/components';
import { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import ConnectConfirm from '../connect-confirm/connect-confirm';
import { CreateFolderModal } from '../create-folder/create-folder';
import { IntFinder } from '../finder/finder';
import { CustomTooltip } from '../integrations.styled';
import ResyncModal from '../resync-modal/resync-modal';
import { useIntegrationsStore } from '../store';
import { useIntegrationContext } from '../useIntegrationContext';
import SharepointDocumentLibrariesModal from '../vendors/sharepoint-document-libraries-modal/sharepoint-document-libraries-modal';
import SharepointSitesModal from '../vendors/sharepoint-sites-modal/sharepoint-sites-modal';
import {
  ConnectButtonWrapper,
  FilePath,
  StyledAlertContainer,
  StyledContainer,
  StyledIntDetails,
  StyledIntDetailsBody,
  StyledIntDetailsBodyGroup,
  StyledIntDetailsBodyGroupTitle,
  StyledIntDetailsBodySection,
  StyledIntDetailsConnectIcon,
  StyledIntDetailsFolder,
  StyledIntDetailsFolderButton,
  StyledIntDetailsFooter,
  StyledIntDetailsHeader,
  StyledIntDetailsHeaderLeft,
  StyledIntDetailsHeaderLeftImg,
  StyledIntDetailsHeaderRight,
  StyledIntDetailsHeaderRightH3,
  StyledIntDetailsHeaderRightLink,
  StyledIntDetailsHeaderRightP,
  StyledSuccessAlertContainer,
} from './storage.styled';

const CloudStorage = () => {
  const {
    isConnected: getIsConnected,
    isLoading,
    connect,
    disconnectAsync,
    disabled,
  } = useIntegrationContext();
  const { mutate: saveMutation, isLoading: isSavingFolder } =
    useStorageFolderMutation();
  const {
    alert,
    setAlert,
    selectedFolder,
    setSelectedFolder,
    setCurrentFolder,
    isFinderOpen,
    isCreateFolderOpen,
    setFinderOpen,
    setCreateFolderOpen,
    setSitePickerOpen,
    setDocumentLibraryPickerOpen,
    site,
    changeSite,
    documentLibrary,
    changeDocumentLibrary,
  } = useIntegrationsStore();
  const navigate = useNavigate();
  const { t } = useTranslation('integrations');
  const { vendor } = useParams<{ vendor: TIntegrationType }>();
  const isConnected = getIsConnected(vendor || 'google');
  const wasConnectedRef = useRef(false);

  const { showModal } = useActionModalStore();

  const onSave = (folderToSave?: IntFolder) => {
    if (alert.open) {
      setAlert({ ...alert, open: false });
    }

    saveMutation(
      {
        vendor: vendor || 'google',
        directory_id: folderToSave?.id || selectedFolder?.id || '',
        site_id: site.id,
        document_id: documentLibrary.id,
      },
      {
        onSuccess: async () => {
          setAlert({
            open: true,
            severity: 'success',
            message: t(`${vendor}.success`),
          });

          await queryClient.invalidateQueries([
            IntegrationKeys.storageSettings,
          ]);
        },
        onError: (error: unknown) => {
          const axiosError = error as AxiosError;

          const message =
            axiosError.status !== 422
              ? t(`${vendor}.error`)
              : t(`${vendor}.folder_not_found`);

          setAlert({
            open: true,
            severity: 'error',
            message,
          });
        },
      },
    );
  };

  const handleSelectFolder = (folder: IntFolder) => {
    setSelectedFolder(folder);
    setFinderOpen(false);
    onSave(folder);
  };

  const handleChangeFolder = async (currentSite = site) => {
    if (vendor === 'sharepoint' && !currentSite.id) {
      const site = await setSitePickerOpen();
      if (!site?.id) return;
    }

    if (selectedFolder) {
      setCurrentFolder(selectedFolder);
    }

    setFinderOpen(true);
  };

  const { data: settings, isLoading: isSettingsLoading } = useStorageSettings(
    vendor || 'google',
    {
      onSuccess: async (res) => {
        const data = res.data;

        const newSite = {
          id: data?.site_id || '',
          name: data?.site_name || '',
          path: '',
          isOpen: false,
        };

        changeSite(newSite);

        changeDocumentLibrary({
          id: data?.document_id || '',
          name: data?.document_name || '',
          path: '',
          isOpen: false,
        });

        if (!data?.folder) {
          handleChangeFolder(newSite);
          return;
        }

        const { folder } = data;

        setSelectedFolder({
          id: folder.id,
          name: folder.name,
          path: folder.path,
        });
      },
      onError: async (error: unknown) => {
        const axiosError = error as { data?: GetIntSettingsResponse };
        const data = axiosError.data as GetIntSettingsResponse;

        setAlert({
          message: data?.message || 'Failed to load integration settings',
          severity: 'error',
          open: true,
        });

        if (data?.data?.folder) {
          setSelectedFolder({
            id: data.data.folder.id,
            name: data.data.folder.name,
            path: data.data.folder.path,
          });
        }

        if (data?.data?.site_id) {
          changeSite({
            id: data.data.site_id || '',
            name: data.data.site_name || '',
            path: '',
            isOpen: false,
          });
        }

        if (data?.data?.document_id) {
          changeDocumentLibrary({
            id: data.data.document_id || '',
            name: data.data.document_name || '',
            path: '',
            isOpen: false,
          });
        }

        return data;
      },
      enabled: isConnected,
    },
  );

  useEffect(() => {
    if (!(settings && settings.data?.site_id)) return;

    changeSite({
      id: settings?.data?.site_id || '',
      name: settings?.data?.site_name || '',
      path: '',
      isOpen: false,
    });
    changeDocumentLibrary({
      id: settings?.data?.document_id || '',
      name: settings?.data?.document_name || '',
      path: '',
      isOpen: false,
    });
  }, [settings, changeSite, changeDocumentLibrary]);

  useEffect(() => {
    // Auto-open appropriate popup after first successful connection if setup is incomplete
    // Only trigger if settings have finished loading and a folder isn't already selected
    if (
      isConnected &&
      !wasConnectedRef.current &&
      !isFinderOpen &&
      !isSettingsLoading &&
      settings
    ) {
      wasConnectedRef.current = true;
      // Use setTimeout to avoid state conflicts during connection
      setTimeout(async () => {
        // For SharePoint: require Site ID first, then Document Library, then Folder
        if (vendor === 'sharepoint') {
          if (!site?.id) {
            // Need to select Site first
            await setSitePickerOpen();
          } else if (!documentLibrary?.id) {
            // Site selected, need Document Library
            await setDocumentLibraryPickerOpen();
          } else if (!selectedFolder?.id) {
            // Both Site and Document Library selected, need Folder
            setFinderOpen(true);
          }
        } else {
          // For other providers (Google Drive, OneDrive, Box): just open folder selector
          if (!selectedFolder?.id) {
            setFinderOpen(true);
          }
        }
      }, 100);
    }
    // Reset when disconnected
    if (!isConnected) {
      wasConnectedRef.current = false;
    }
  }, [
    isConnected,
    isFinderOpen,
    isSettingsLoading,
    settings,
    selectedFolder?.id,
    site?.id,
    documentLibrary?.id,
    vendor,
    setFinderOpen,
    setSitePickerOpen,
    setDocumentLibraryPickerOpen,
  ]);

  useEffect(() => {
    // reset on unmount
    return () => {
      setAlert({
        open: false,
        severity: 'success',
        message: '',
      });
    };
  }, [setAlert]);

  const handleCloseFinder = () => {
    setFinderOpen(false);
  };

  const handleDisconnect = async () => {
    const integrationName = t(`${vendor}.name`);

    const { isConfirmed } = await showModal({
      header: t('disconnect.modal.header', { provider: integrationName }),
      message: 'disconnect.modal.message',
      messageParams: { provider: integrationName },
      translationNamespace: 'integrations',
      closeButtonLabel: t('disconnect.modal.cancel_btn') ?? '',
      submitButtonLabel: t('disconnect.modal.confirm_btn') ?? '',
    });

    if (!isConfirmed) {
      return;
    }

    try {
      await disconnectAsync();

      setAlert({
        message: `${integrationName} was successfully disconnected!`,
        severity: 'warning',
        open: true,
      });
    } catch {
      setAlert({
        message: t('setup.disconnect_error', { provider: integrationName }),
        severity: 'error',
        open: true,
      });
    }
  };

  const onCloseCreateFolder = () => {
    setCreateFolderOpen(false);
    setFinderOpen(true);
  };

  const handleAlertClose = () => {
    setAlert({ ...alert, open: false });
  };

  const handleNavigate = async () => {
    if (
      isConnected &&
      (!selectedFolder?.id || (vendor === 'sharepoint' && !site?.id))
    ) {
      const { isConfirmed } = await showModal({
        header: (t('setup.incomplete_title') ?? 'Setup Incomplete') as string,
        message: (t('setup.incomplete_message') ??
          'Your changes have not been saved. Are you sure you want to leave?') as string,
        closeButtonLabel: (t('setup.cancel_btn') ?? 'Cancel') as string,
        submitButtonLabel: (t('setup.leave_btn') ?? 'Leave') as string,
      });

      if (!isConfirmed) return;
    }

    setAlert({ ...alert, open: false });
    setTimeout(() => {
      navigate('/integrations');
    }, 100);
  };

  return (
    <>
      <StyledContainer>
        {(!selectedFolder?.id || (vendor === 'sharepoint' && !site?.id)) && (
          <StyledSuccessAlertContainer>
            <Alert severity="warning">{t('setup.incomplete_warning')}</Alert>
          </StyledSuccessAlertContainer>
        )}
        {alert.open && (
          <StyledSuccessAlertContainer>
            <Alert
              data-cy="alert"
              severity={alert.severity}
              onClose={handleAlertClose}
            >
              <p>{alert.message}</p>
            </Alert>
          </StyledSuccessAlertContainer>
        )}
        <StyledIntDetails>
          <ModalCloseButton
            data-cy={`integration-${vendor}-close`}
            aria-label="close"
            onClick={handleNavigate}
          >
            <FontAwesomeIcon icon={faXmark} />
          </ModalCloseButton>
          <StyledIntDetailsHeader>
            <StyledIntDetailsHeaderLeft>
              <StyledIntDetailsHeaderLeftImg
                src={`/assets/int-${vendor}.png`}
                alt={t(`${vendor}.thumbnail.title`) || ''}
              />
            </StyledIntDetailsHeaderLeft>
            <StyledIntDetailsHeaderRight>
              <StyledIntDetailsHeaderRightH3>
                {t(`${vendor}.thumbnail.title`)}
              </StyledIntDetailsHeaderRightH3>
              <StyledIntDetailsHeaderRightP>
                {t(`${vendor}.thumbnail.desc`)}
              </StyledIntDetailsHeaderRightP>
              <StyledIntDetailsHeaderRightLink
                href={t(`${vendor}.url`) || '#'}
                target="_blank"
              >
                {t(`${vendor}.link`)}
              </StyledIntDetailsHeaderRightLink>
            </StyledIntDetailsHeaderRight>
          </StyledIntDetailsHeader>
          <StyledIntDetailsBody>
            {isConnected ? (
              <>
                <StyledIntDetailsBodySection>
                  <StyledIntDetailsBodyGroup>
                    <LoadingButton
                      variant="outlined"
                      color="tertiary"
                      startIcon={
                        <StyledIntDetailsConnectIcon icon={faUnlink} />
                      }
                      onClick={handleDisconnect}
                      loading={isLoading}
                      data-cy={`disconnect-${vendor}`}
                    >
                      Disconnect
                    </LoadingButton>
                  </StyledIntDetailsBodyGroup>
                </StyledIntDetailsBodySection>
                <StyledIntDetailsBodySection>
                  {alert.open && alert.severity === 'error' && (
                    <StyledAlertContainer>
                      <Alert
                        data-cy="alert"
                        severity={alert.severity}
                        onClose={() => setAlert({ ...alert, open: false })}
                      >
                        <p>{alert.message}</p>
                      </Alert>
                    </StyledAlertContainer>
                  )}
                  <div>
                    <StyledIntDetailsBodyGroupTitle>
                      {t(`${vendor}.title`)}
                    </StyledIntDetailsBodyGroupTitle>
                    <p>{t(`${vendor}.desc`)}</p>
                  </div>
                  <StyledIntDetailsFolder>
                    <>
                      {(selectedFolder?.id || site?.name) && (
                        <FilePath title={selectedFolder?.path}>
                          <FontAwesomeIcon icon={faFolder} />
                          <span>
                            {(
                              site.name +
                              '/' +
                              (documentLibrary.name || '') +
                              selectedFolder?.path
                            ).replace(/\/\//g, '/')}
                          </span>
                        </FilePath>
                      )}
                      <StyledIntDetailsFolderButton
                        variant="contained"
                        color="secondary"
                        data-cy={`change-${vendor}-folder`}
                        onClick={handleChangeFolder}
                      >
                        {t(`${vendor}.choose_btn`)}
                      </StyledIntDetailsFolderButton>
                    </>
                  </StyledIntDetailsFolder>
                </StyledIntDetailsBodySection>
                <StyledIntDetailsFooter>
                  <Button
                    data-cy="back-to-integrations"
                    variant="outlined"
                    color="tertiary"
                    onClick={handleNavigate}
                  >
                    Back
                  </Button>
                  <LoadingButton
                    data-cy={`save-${vendor}-folder`}
                    onClick={() => onSave()}
                    variant="contained"
                    color="primary"
                    disabled={
                      !selectedFolder?.id && !site.id && !documentLibrary.id
                    }
                    loading={isSavingFolder}
                  >
                    Save Changes
                  </LoadingButton>
                </StyledIntDetailsFooter>
              </>
            ) : (
              <CustomTooltip
                title={t(
                  `tooltip.level.${
                    ['sharepoint', 'google_account'].includes(vendor ?? '')
                      ? 'account'
                      : 'user'
                  }`,
                )}
                placement="top"
              >
                <ConnectButtonWrapper>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    startIcon={<StyledIntDetailsConnectIcon icon={faLink} />}
                    onClick={() => connect(vendor || 'google')}
                    loading={isLoading}
                    data-cy={`connect-${vendor}`}
                    disabled={disabled[vendor || 'google'] || isLoading}
                  >
                    Connect
                  </LoadingButton>
                </ConnectButtonWrapper>
              </CustomTooltip>
            )}
          </StyledIntDetailsBody>
        </StyledIntDetails>
      </StyledContainer>
      <IntFinder
        open={isFinderOpen}
        onClose={handleCloseFinder}
        onSelect={handleSelectFolder}
      />
      <CreateFolderModal
        open={isCreateFolderOpen}
        onClose={onCloseCreateFolder}
      />
      <ConnectConfirm />
      <SharepointSitesModal />
      <SharepointDocumentLibrariesModal />
      <ActionModal />
      <ResyncModal />
    </>
  );
};

export const CloudStorageWrapper = () => {
  const { withPermissions } = usePermissions();
  const { vendor } = useParams<{ vendor: TIntegrationType }>();

  // Check permissions based on integration type
  const permissionMap: Record<TIntegrationType, PermissionsKeys> = {
    google: PermissionsKeys.SHOW_GOOGLE_DRIVE_INTEGRATION,
    microsoft: PermissionsKeys.SHOW_ONEDRIVE_BUSINESS_INTEGRATION,
    box: PermissionsKeys.SHOW_BOX_INTEGRATION,
    sharepoint: PermissionsKeys.SHOW_SHAREPOINT_INTEGRATION,
    google_account: PermissionsKeys.SHOW_GOOGLE_DRIVE_ACCOUNT_INTEGRATION,
    box_account: PermissionsKeys.SHOW_BOX_ACCOUNT_INTEGRATION,
    hubspot: PermissionsKeys.GET_EMBEDDED_HUBSPOT_LINK,
    salesforce: PermissionsKeys.GET_EMBEDDED_SALESFORCE_LINK,
  };

  const requiredPermission = vendor ? permissionMap[vendor] : null;

  if (requiredPermission && !withPermissions(requiredPermission)) {
    return <Navigate to="/integrations" />;
  }

  return <CloudStorage />;
};

export default CloudStorage;
